import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';

import {
  CommentHeatmapClusterRow,
  CommentHeatmapPayload,
  CommentHeatmapPoint,
} from './types';
import {
  PLOTLY_LAYOUT_BASE,
  TimeWindowKey,
  colorForIndex,
  cutoffForWindow,
  filterByCreatedAt,
  formatRating,
  truncate,
} from './chartHelpers';
import TimeWindowSelector from './TimeWindowSelector';

interface Props {
  title: string;
  description?: string;
  payload?: CommentHeatmapPayload | null;
  generatedAt: string | null;
  /**
   * If true the per-comment rating shows up in the hover tooltip and a
   * per-cluster average-rating column is rendered. Streams without
   * ratings (Characteristic corrections) should set this to false.
   */
  showRating?: boolean;
  /** Extra hover columns: label + accessor on a heatmap point. */
  extraHoverFields?: { label: string; pick: (p: CommentHeatmapPoint) => string | null | undefined }[];
}

const UNCLUSTERED_ID = -1;

const CommentHeatmapCard: React.FC<Props> = ({
  title,
  description,
  payload,
  generatedAt,
  showRating = true,
  extraHoverFields = [],
}) => {
  const [windowKey, setWindowKey] = useState<TimeWindowKey>('all');

  const pca = payload || { points: [], explained_variance: [], sample_size: 0, clusters: [] };
  const allPoints = pca.points || [];
  const clusterRows: CommentHeatmapClusterRow[] = pca.clusters || [];

  const clusterLookup = useMemo(() => {
    const m = new Map<number, CommentHeatmapClusterRow>();
    clusterRows.forEach(c => m.set(c.cluster_id, c));
    return m;
  }, [clusterRows]);

  const filteredPoints = useMemo(() => {
    const cutoff = cutoffForWindow(generatedAt, windowKey);
    return filterByCreatedAt(allPoints, cutoff);
  }, [allPoints, generatedAt, windowKey]);

  const explained = (pca.explained_variance || []).map(v => `${(v * 100).toFixed(1)}%`);

  const labelForCluster = (cid: number): string => {
    const summary = clusterLookup.get(cid);
    const keywords = summary?.label_keywords?.filter(Boolean) || [];
    if (keywords.length > 0) return keywords.slice(0, 3).join(', ');
    if (cid === UNCLUSTERED_ID) return 'unclustered';
    return `Cluster ${cid}`;
  };

  // Live per-cluster stats computed from the filtered subset, so the
  // panel reflects whatever the user is actually looking at.
  const filteredClusterStats = useMemo(() => {
    const buckets = new Map<number, { count: number; ratings: number[] }>();
    filteredPoints.forEach(p => {
      const cid = p.cluster_id ?? UNCLUSTERED_ID;
      let entry = buckets.get(cid);
      if (!entry) {
        entry = { count: 0, ratings: [] };
        buckets.set(cid, entry);
      }
      entry.count += 1;
      if (typeof p.rating === 'number') entry.ratings.push(p.rating);
    });
    return buckets;
  }, [filteredPoints]);

  const traces = useMemo(() => {
    if (filteredPoints.length === 0) return [] as any[];

    const xs = filteredPoints.map(p => p.x);
    const ys = filteredPoints.map(p => p.y);

    const density = {
      type: 'histogram2dcontour',
      x: xs,
      y: ys,
      colorscale: 'Blues',
      reversescale: false,
      showscale: false,
      contours: { coloring: 'fill' },
      ncontours: 16,
      opacity: 0.45,
      hoverinfo: 'skip',
    };

    // Group points by cluster so each cluster becomes its own scatter
    // trace with a stable colour from the palette.
    const groups = new Map<number, CommentHeatmapPoint[]>();
    filteredPoints.forEach(p => {
      const cid = p.cluster_id ?? UNCLUSTERED_ID;
      if (!groups.has(cid)) groups.set(cid, []);
      groups.get(cid)!.push(p);
    });

    const sortedClusterIds = Array.from(groups.keys()).sort((a, b) => a - b);

    const buildHover = (point: CommentHeatmapPoint): (string | number)[] => {
      const base: (string | number)[] = [
        truncate(point.comment, 220),
        showRating ? (point.rating ?? '\u2014') : '\u2014',
        point.created_at ? new Date(point.created_at).toLocaleDateString() : '\u2014',
        labelForCluster(point.cluster_id ?? UNCLUSTERED_ID),
      ];
      for (const field of extraHoverFields) {
        const value = field.pick(point);
        base.push(value || '\u2014');
      }
      return base;
    };

    const hoverLines = [
      '<b>%{customdata[0]}</b>',
      ...(showRating ? ['rating: %{customdata[1]}'] : []),
      'created: %{customdata[2]}',
      'cluster: %{customdata[3]}',
      ...extraHoverFields.map((field, idx) => `${field.label}: %{customdata[${4 + idx}]}`),
    ].join('<br>');

    const scatterTraces = sortedClusterIds.map((cid, idx) => {
      const points = groups.get(cid)!;
      const summary = clusterLookup.get(cid);
      const keywordLabel = labelForCluster(cid);
      const stats = filteredClusterStats.get(cid);
      const ratingForLabel =
        stats && stats.ratings.length > 0
          ? stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length
          : summary?.avg_rating ?? null;

      const ratingSuffix =
        showRating && ratingForLabel !== null && ratingForLabel !== undefined
          ? ` (avg ${formatRating(ratingForLabel)})`
          : '';

      return {
        type: 'scattergl',
        mode: 'markers',
        x: points.map(p => p.x),
        y: points.map(p => p.y),
        name: `${keywordLabel}${ratingSuffix}`,
        marker: {
          size: 7,
          color: colorForIndex(idx),
          opacity: 0.85,
          line: { color: '#1f2937', width: 0.4 },
        },
        customdata: points.map(buildHover),
        hovertemplate: hoverLines + '<extra></extra>',
      } as any;
    });

    return [density, ...scatterTraces];
  }, [
    filteredPoints,
    showRating,
    extraHoverFields,
    clusterLookup,
    filteredClusterStats,
  ]);

  const renderPlot = () => {
    if (pca.warning) {
      return <p className="text-sm text-gray-500">{pca.warning}</p>;
    }
    if (allPoints.length === 0) {
      return <p className="text-sm text-gray-500">No comment embeddings available yet.</p>;
    }
    if (filteredPoints.length === 0) {
      return (
        <p className="text-sm text-gray-500">
          No comments fall inside the selected window.
        </p>
      );
    }
    return (
      <Plot
        data={traces as any}
        layout={{
          ...PLOTLY_LAYOUT_BASE,
          height: 480,
          xaxis: { title: 'PC1', zeroline: false },
          yaxis: { title: 'PC2', zeroline: false },
          showlegend: true,
          legend: { orientation: 'v', x: 1.02, y: 1 },
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: '100%' }}
        useResizeHandler
      />
    );
  };

  const renderClusterPanel = () => {
    if (filteredPoints.length === 0) return null;
    const sortedIds = Array.from(filteredClusterStats.keys()).sort((a, b) => a - b);
    if (sortedIds.length === 0) return null;
    return (
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1 pr-3 font-medium">Cluster</th>
              <th className="py-1 pr-3 font-medium">Count</th>
              {showRating && (
                <>
                  <th className="py-1 pr-3 font-medium">Avg rating</th>
                  <th className="py-1 pr-3 font-medium">Rated</th>
                </>
              )}
              <th className="py-1 font-medium">Keywords</th>
            </tr>
          </thead>
          <tbody>
            {sortedIds.map((cid, idx) => {
              const stats = filteredClusterStats.get(cid)!;
              const summary = clusterLookup.get(cid);
              const avg =
                stats.ratings.length > 0
                  ? stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length
                  : null;
              const dotColor = colorForIndex(idx);
              const keywords = summary?.label_keywords?.filter(Boolean) || [];
              return (
                <tr key={cid} className="border-b border-gray-100">
                  <td className="py-1 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: dotColor }}
                      />
                      <span>{labelForCluster(cid)}</span>
                    </span>
                  </td>
                  <td className="py-1 pr-3 tabular-nums">{stats.count.toLocaleString()}</td>
                  {showRating && (
                    <>
                      <td className="py-1 pr-3 tabular-nums">
                        {avg !== null ? formatRating(avg) : '\u2014'}
                      </td>
                      <td className="py-1 pr-3 tabular-nums text-gray-500">
                        {stats.ratings.length}
                      </td>
                    </>
                  )}
                  <td className="py-1 text-gray-600">
                    {keywords.length > 0 ? keywords.join(', ') : '\u2014'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h3 className="text-lg font-semibold">{title}</h3>
        <TimeWindowSelector value={windowKey} onChange={setWindowKey} />
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {description ||
          'Each comment is embedded with mistral-embed and projected to 2D via PCA. The blue contour shows comment density; dots are individual comments coloured by KMeans cluster. The PCA basis + clusters are fitted on the full comment set (' +
            (pca.sample_size || 0).toLocaleString() +
            ' embeddings) so windows stay comparable.'}
        {explained.length === 2 && ` Explained variance: PC1 ${explained[0]}, PC2 ${explained[1]}.`}
        {' Showing '}
        <span className="font-medium text-gray-700">
          {filteredPoints.length.toLocaleString()} of {allPoints.length.toLocaleString()}
        </span>
        {' comments.'}
      </p>
      {renderPlot()}
      {renderClusterPanel()}
    </div>
  );
};

export default CommentHeatmapCard;
